import { useEffect } from "react"
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom"

import useRedirectOnBeIdChange from '../../../hooks/useRedirectOnBeIdChange.js'
import { useComputed, useSignal } from "@preact/signals-react"

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Nav from 'react-bootstrap/Nav'
import Table from 'react-bootstrap/Table'

import AdministrationTabs from '../components/AdministrationTabs.jsx'
import Icon from "../../../components/Icon.jsx"
import Loadable from "../../../components/Loadable.jsx"
import PageTitle from "../../../components/PageTitle.jsx"
import { SystemUserType, SortDirectionType } from "../../../utils/constants.js"

import { businessEntitiesLoadedSignal, businessEntitiesErrorSignal, businessEntityDictionarySignal } from "../../../state/signals/businessEntitySignals.js"
import { userSignal, userPermissionsSignal, userPermissionsLoadedSignal, userPermissionsErrorSignal } from "../../../state/signals/userSignals.js"
import { fetchUser, fetchUserPermissions } from "../../../state/actions/userActions.js"


export default function EditSystemUserPermissionsPage() {

    const { beId, userId, systemUserType } = useParams()
    useRedirectOnBeIdChange(beId, `/be/${beId}/management/systemUsers/${systemUserType}`)
    const { userPermissionsSummary, config, accessToken, debug, accordionData } = useOutletContext()
    const pageTabSignal = useSignal("permissions")

    useEffect(() => {
        fetchUser(userId, config, accessToken)
        fetchUserPermissions(userId, config, accessToken)
    }, [userId, config, accessToken])

    const canEditComputed = useComputed(() => {
        return userPermissionsSummary?.value?.canEditUsers
    })

    function PermissionTables({ userPermissions }) {
        if (userPermissions.length == 0) {
            return <div>No permissions to display</div>
        }
        let divs = []
        for (let i = 0; i < userPermissions.length; i++) {
            const item = userPermissions[i]
            const applicationGroupPermissions = item.applicationGroupPermissions ?? []
            const businessEntityPermissions = applicationGroupPermissions.find(x => x.applicationGroupId === 3)?.businessEntityPermissions ?? []
            let content
            if (businessEntityPermissions.length > 0) {
                content = (
                    <Table responsive striped hover size="sm" className="cc-table">
                        <thead>
                            <tr>
                                <th>Account Type</th>
                                <th>View</th>
                                <th>Enabled</th>
                                <th>Edit</th>
                                <th>Grant</th>
                            </tr>
                        </thead>
                        <tbody>
                            <PermissionTableRows items={businessEntityPermissions} />
                        </tbody>
                    </Table>
                )
            } else {
                content = <>No permissions to display for this application group.</>
            }
            let name
            let key
            if ('businessEntityId' in item) {
                if (item.businessEntityId in businessEntityDictionarySignal.value) {
                    const businessEntity = businessEntityDictionarySignal.value[item.businessEntityId]
                    name = businessEntity.name
                } else {
                    name = `Unknown business entity ${item.businessEntityId}`
                }
                key = item.businessEntityId
            } else {
                name = "Error"
                key = `error-${i}`
            }
            divs.push(
                <div key={key} className="mx-3">
                    <h4>{name}</h4>
                    {content}
                </div>
            )
        }
        return divs
    }

    function Checkmark(){
        return <Icon icon="check2" height={16} width={16} />
    }

    function X(){
        return <Icon icon="x" height={16} width={16} />
    }

    function PermissionTableRows({ items }) {
        if (!items || !items.length) {
            return <tr><td colSpan="5" className="text-center">No permissions to display for this business entity.</td></tr>
        }
        let rows = []
        for (let i = 0; i < items.length; i++) {
            const item = items[i]
            if (!('id' in item)) {
                throw new Error("Undefined permission ID")
            }
            const idx = item.id
            rows.push(
                <tr key={idx}>
                    <td>{item.name}</td>
                    {
                        item.view === true ? (
                            <td style={{color:"green"}}><Checkmark />View</td>
                        ) : (
                            <td style={{color:"red"}}><X /></td>
                        )
                    }
                    {
                        item.enabled === true ? (
                            <td style={{color:"green"}}><Checkmark />Enabled</td>
                        ) : (
                            <td style={{color:"red"}}><X /></td>
                        )
                    }
                    {
                        item.edit === true ? (
                            <td style={{color:"green"}}><Checkmark />Edit</td>
                        ) : (
                            <td style={{color:"red"}}><X /></td>
                        )
                    }
                    {
                        item.grant === true ? (
                            <td style={{color:"green"}}><Checkmark />Grant</td>
                        ) : (
                            <td style={{color:"red"}}><X /></td>
                        )
                    }
                    {/*Old version*/}
                    {/*
                    <td>{item.enabled === true ? "Enabled" : ""}</td>
                    <td>{item.enabled === true ? "Enabled" : ""}</td>
                    <td>{item.edit === true ? "Edit" : ""}</td>
                    <td>{item.grant === true ? "Grant" : ""}</td>
                    */}
                </tr>
            )
        }
        return <>{rows}</>
    }

    return (
        <>
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/management/accounts` }}>Administration</Breadcrumb.Item>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/management/systemUsers/${systemUserType}` }}>Users</Breadcrumb.Item>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/management/systemUsers/${systemUserType}/${userId}/` }}>{userSignal.value ? userSignal.value.username : "Loading..."}</Breadcrumb.Item>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/management/systemUsers/${systemUserType}/${userId}/permissions` }} active>Permissions</Breadcrumb.Item>
            </Breadcrumb>
            <PageTitle title='User Permissions' canEditSignal={canEditComputed} />
            <AdministrationTabs activeKey="5" />
            <Nav
                variant="tabs"
                size="sm"
                className="px-3 my-3"
                activeKey={pageTabSignal.value}
            >
                <Nav.Item>
                    <Link to={`/be/${beId}/management/systemUsers/${systemUserType}/${userId}`} eventKey="profile" className="nav-link">Profile</Link>
                </Nav.Item>
                <Nav.Item>
                    <Link to={`/be/${beId}/management/systemUsers/${systemUserType}/${userId}/permissions`} eventKey="permissions" className="nav-link active">Permissions</Link>
                </Nav.Item>
            </Nav>
            {
                (pageTabSignal.value === "permissions") && (
                    <Loadable loadedSignals={[businessEntitiesLoadedSignal, userPermissionsLoadedSignal]} errorSignals={[businessEntitiesErrorSignal, userPermissionsErrorSignal]}>
                        <PermissionTables userPermissions={userPermissionsSignal.value} />
                    </Loadable>
                )
            }
        </>
    )
}